import { FC, useContext } from "react";
import FormFieldsContainer from "../../../../components/ui/FormFieldsContainer";
import useImageUploader from "../../../../components/ui/ImageUploader";
import { Form, ModalDialog } from "@rothoblaas/design-system";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../../../context/notificationContext";
import useI18n from "../../../../hooks/useTranslations";
import { GroovingType } from "../../../../http/deckDesigner/types/groovingTypes";
import useDeckGroovingTypesApi from "../../../../http/deckDesigner/useGroovingTypes";
import TranslatableTextField from "../../../../components/ui/TranslatableTextField";

interface GroovingTypeFormBody {
  name: string | null;
  nameIt: string | null;
  nameDe: string | null;
  nameEs: string | null;
  nameFr: string | null;
}

interface GroovingTypeFormProps {
  groovingType: GroovingType;
  onClose: () => void;
}

const GroovingTypeForm: FC<GroovingTypeFormProps> = ({ groovingType, onClose }) => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const groovingTypesApi = useDeckGroovingTypesApi();

  const defaultValues: GroovingTypeFormBody = {
    name: groovingType.name ?? null,
    nameIt: groovingType.nameIt ?? null,
    nameDe: groovingType.nameDe ?? null,
    nameEs: groovingType.nameEs ?? null,
    nameFr: groovingType.nameFr ?? null,
  };

  const { component, newImage, removeImage, onSave } = useImageUploader({
    defaultImage: groovingType.image ?? undefined,
    isLoading: false,
  });

  const onSubmit = () => async (formData: GroovingTypeFormBody) => {
    try {
      const image = await groovingTypesApi.uploadImage(groovingType.code, newImage, removeImage);
      await groovingTypesApi.updateDeckGroovingType(groovingType.code, {
        name: formData.name ?? "",
        nameIt: formData.nameIt || null,
        nameDe: formData.nameDe || null,
        nameEs: formData.nameEs || null,
        nameFr: formData.nameFr || null,
        ...image,
      });

      onSave();
      dispatch(showSuccessNotification(i18n.translation));
      onClose();
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  return (
    <ModalDialog
      onClose={onClose}
      title={`${i18n.translation.common.edit} ${i18n.translation.deckDesignerModule.groovingTypes.singular}`}
    >
      <Form<GroovingTypeFormBody>
        submitText={i18n.translation.common.save}
        onSubmit={onSubmit}
        defaultValues={defaultValues}
      >
        {(form) => (
          <FormFieldsContainer fullWidth imageUploader={component}>
            <TranslatableTextField form={form} />
          </FormFieldsContainer>
        )}
      </Form>
    </ModalDialog>
  );
};

export default GroovingTypeForm;
